/**
 * A class to encrypt and decrypt text using AES-GCM
 * @example
 * const cryptoStorage = new CryptoStorage('password')
 * const encrypted = await cryptoStorage.encrypt('Hello, world!')
 * const decrypted = await cryptoStorage.decrypt(encrypted)
 */
export class CryptoStorage {
	private readonly password: string

	private readonly salt: Uint8Array<ArrayBuffer>

	/**
	 * Create a new CryptoStorage instance
	 * @param password Password to use for encryption
	 */
	constructor(password: string) {
		this.password = password
		this.salt = new Uint8Array(new TextEncoder().encode('rtikHBDKkjGfrJKmncDtJLisxe3$^(08^52!#YiknC@#ThNnFyJVE57&HVV'))
	}

	// Generate an AES-GCM encryption key from the password
	private async getKey(): Promise<CryptoKey> {
		const keyMaterial = await crypto.subtle.importKey(
			'raw',
			new TextEncoder().encode(this.password),
			{ name: 'PBKDF2' },
			false,
			['deriveKey']
		)

		const params: Pbkdf2Params = {
			name: 'PBKDF2',
			salt: this.salt,
			iterations: 100000,
			hash: 'SHA-256',
		}

		return crypto.subtle.deriveKey(params, keyMaterial, { name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt'])
	}

	/**
	 * Encrypt a text using AES-GCM
	 * @param text Text to encrypt
	 * @returns Encrypted text in Base64 format
	 */
	public async encrypt(text: string): Promise<string> {
		const key = await this.getKey()
		const iv = crypto.getRandomValues(new Uint8Array(12)) // AES-GCM requires a 12-byte IV

		const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: iv }, key, new TextEncoder().encode(text))

		// Concatenate IV + encrypted data and convert to Base64
		const encryptedArray = new Uint8Array(encryptedData)
		const combinedArray = new Uint8Array(iv.length + encryptedArray.length)

		combinedArray.set(iv)
		combinedArray.set(encryptedArray, iv.length)

		return btoa(String.fromCharCode(...combinedArray))
	}

	/**
	 * Decrypt an encrypted text using AES-GCM
	 * @param encryptedBase64 Encrypted text in Base64 format
	 * @returns Decrypted text
	 */
	public async decrypt(encryptedBase64: string): Promise<string> {
		const key = await this.getKey()
		const encryptedArray = Uint8Array.from(atob(encryptedBase64), char => char.charCodeAt(0))
		const iv = encryptedArray.slice(0, 12)
		const data = encryptedArray.slice(12)
		const decryptedData = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv }, key, data)

		return new TextDecoder().decode(decryptedData)
	}
}
